// TODO: Add seeking funding filter back when seekingFunding functionality is in prod
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProjectStatusConstants } from "../../../../../constants";
import { BadgeData, SelectData } from "../../../../../models";
import { searchProjects, SearchProjectsRequest, SearchProjectsResponse } from "../../../../../service/publicquery";
import { FilterGroups, SortDirection, Status } from "../../../../../service/Shared";
import { getCountryNameByCode, useDebounce } from "../../../../../utils";
import { getPublicProjectOverviewRoute } from "../../../../../utils/routes";
import { CheckboxFilterValueType, DataFilterValueType, Filter } from "../../../../../widget";

type DisplayProject = {
  uuid: string;
  imageSource: string | null;
  hasUnits?: boolean;
  hasPriceInDirectory?: boolean;
  seekingFunding?: boolean;
  pilotProject?: boolean;
  name: string;
  addressCountryCode: string;
  developerSmallLogoFileUrl: string | null;
  developerDisplayName?: string;
  standardDisplayName?: string;
  listingContentShortDescription: string;
  tags?: string[];
  badges?: BadgeData[];
  certifications?: BadgeData[];
  calculatedIssuancePriceMin: number | null;
  calculatedIssuancePriceMax: number | null;
  calculatedTotalQuantityMin: number | null;
  calculatedTotalQuantityMax: number | null;
  cachedTotalQuantity: number | null;
  isCodeCompliant: boolean;
  quantityType: string | null;
};

interface ProjectsFilter extends DataFilterValueType {
  search?: string | null;
  unitsNumber?: number[] | null;
  unitsSale?: CheckboxFilterValueType | null;
  unitPrice?: number[] | null;
  unitType?: CheckboxFilterValueType | null;
  vintage?: number[] | null;
  code?: CheckboxFilterValueType | null;
  status?: string | null;
  developer?: string | null;
  projectType?: string | null;
  badges?: string | null;
  certifications?: string | null;
  countryCode?: string | null;
  region?: string | null;
  tags?: string | null;
  group?: string | null;
  // seekingFunding?: CheckboxFilterValueType | null;
  pilotProject?: CheckboxFilterValueType | null;
  unitMeasurements?: CheckboxFilterValueType | null;
  habitatType?: string | null;
  habitatCondition?: string | null;
}

interface UseProjectsSearchReturnData {
  filters: Filter[];
  currentFilter: ProjectsFilter;
  currentSort: string;
  sorting: SelectData;
  projects: DisplayProject[];
  onFiltersChange: (value: DataFilterValueType) => void;
  onSortingChange: (value: string) => void;
  onProjectClick: (project: DisplayProject) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  totalCount: number;
}

export const useProjectsSearch = (developerUuid?: string | null, codeUuid?: string): UseProjectsSearchReturnData => {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [aspects, setAspects] = useState<SearchProjectsResponse["aspects"]>();
  const [projects, setProjects] = useState<DisplayProject[]>([]);
  const [pageSize] = useState<number>(8);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sorting] = useState<SelectData>([
    {
      key: "results.dataScore|desc",
      value: "Default",
    },
    {
      key: "results.displayName|asc",
      value: "Name (asc)",
    },
    {
      key: "results.displayName|desc",
      value: "Name (desc)",
    },
    {
      key: "results.developer.displayName|asc",
      value: "Developer (asc)",
    },
    {
      key: "results.developer.displayName|desc",
      value: "Developer (desc)",
    },
    {
      key: "results.addressCountryCode|asc",
      value: "Country (asc)",
    },
    {
      key: "results.addressCountryCode|desc",
      value: "Country (desc)",
    },
    {
      key: "results.addressRegion|asc",
      value: "County / Region / State (asc)",
    },
    {
      key: "results.addressRegion|desc",
      value: "County / Region / State (desc)",
    },
    {
      key: "results.standard.displayName|asc",
      value: "Code (asc)",
    },
    {
      key: "results.standard.displayName|desc",
      value: "Code (desc)",
    },
    {
      key: "results.calculatedTotalQuantityMin|asc",
      value: "Number of units (asc)",
    },
    {
      key: "results.calculatedTotalQuantityMin|desc",
      value: "Number of units (desc)",
    },
  ]);
  const [currentSort, setCurrentSort] = useState<string>(sorting[0].key as string);
  const [currentFilter, setCurrentFilter] = useState<ProjectsFilter>({});

  const navigate = useNavigate();
  const debounceValue = useDebounce(currentFilter);
  const [searchParams, setSearchParams] = useSearchParams();
  const [startCursor, setStartCursor] = useState<string>();
  const [endCursor, setEndCursor] = useState<string>();
  const [beforeCursor, setBeforeCursor] = useState<string | undefined>(searchParams.get("beforeCursor") || undefined);
  const [afterCursor, setAfterCursor] = useState<string | undefined>(searchParams.get("afterCursor") || undefined);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);
  const onPrevious = (): void => {
    setBeforeCursor(startCursor);
    setAfterCursor(undefined);
  };
  const onNext = (): void => {
    setBeforeCursor(undefined);
    setAfterCursor(endCursor);
  };

  useEffect(() => {
    currentFilter.search = searchParams.get("search");
    currentFilter.status = searchParams.get("status");
    currentFilter.developer = searchParams.get("developer");
    currentFilter.projectType = searchParams.get("projectType");
    currentFilter.badges = searchParams.get("badges");
    currentFilter.certifications = searchParams.get("certifications");
    currentFilter.countryCode = searchParams.get("countryCode");
    currentFilter.region = searchParams.get("region");
    currentFilter.tags = searchParams.get("tags");
    currentFilter.group = searchParams.get("group");
    currentFilter.habitatType = searchParams.get("habitatType");
    currentFilter.habitatCondition = searchParams.get("habitatCondition");

    const unitsNumber = searchParams.get("unitsNumber");

    if (unitsNumber) {
      const [calculatedTotalQuantityMin, calculatedTotalQuantityMax] = unitsNumber.split(",");
      currentFilter.unitsNumber = [parseInt(calculatedTotalQuantityMin, 10), parseInt(calculatedTotalQuantityMax, 10)];
    }

    const unitsSale = searchParams.get("unitsSale");

    if (unitsSale) {
      currentFilter.unitsSale = unitsSale.split(",").reduce<CheckboxFilterValueType>((accumulator, current) => {
        const [key, value] = current.split(":");
        accumulator[key] = value === "true";

        return accumulator;
      }, {});
    }

    // const seekingFunding = searchParams.get("seekingFunding");

    // if (seekingFunding) {
    //   currentFilter.seekingFunding = seekingFunding
    //     .split(",")
    //     .reduce<CheckboxFilterValueType>((accumulator, current) => {
    //       const [key, value] = current.split(":");
    //       accumulator[key] = value === "true";

    //       return accumulator;
    //     }, {});
    // }

    const pilotProject = searchParams.get("pilotProject");

    if (pilotProject) {
      currentFilter.pilotProject = pilotProject.split(",").reduce<CheckboxFilterValueType>((accumulator, current) => {
        const [key, value] = current.split(":");
        accumulator[key] = value === "true";

        return accumulator;
      }, {});
    }

    const unitPrice = searchParams.get("unitPrice");

    if (unitPrice) {
      const [calculatedIssuancePriceMin, calculatedIssuancePriceMax] = unitPrice.split(",");
      currentFilter.unitPrice = [parseInt(calculatedIssuancePriceMin, 10), parseInt(calculatedIssuancePriceMax, 10)];
    }

    const unitType = searchParams.get("unitType");

    if (unitType) {
      currentFilter.unitType = unitType.split(",").reduce<CheckboxFilterValueType>((accumulator, current) => {
        const [key, value] = current.split(":");
        accumulator[key] = value === "true";

        return accumulator;
      }, {});
    }

    const unitMeasurements = searchParams.get("unitMeasurements");

    if (unitMeasurements) {
      currentFilter.unitMeasurements = unitMeasurements
        .split(",")
        .reduce<CheckboxFilterValueType>((accumulator, current) => {
          accumulator[current] = true;

          return accumulator;
        }, {});
    }

    const vintage = searchParams.get("vintage");

    if (vintage) {
      const [calculatedVintageEndDateMin, calculatedVintageEndDateMax] = vintage.split(",");
      currentFilter.vintage = [
        parseInt(calculatedVintageEndDateMin.split("-")[0], 10),
        parseInt(calculatedVintageEndDateMax.split("-")[0], 10),
      ];
    }

    const code = searchParams.get("code");

    if (code) {
      currentFilter.code = code.split(",").reduce<CheckboxFilterValueType>((accumulator, current) => {
        const [key, value] = current.split(":");
        accumulator[key] = value === "true";

        return accumulator;
      }, {});
    }

    setCurrentFilter({ ...currentFilter });

    const sort = searchParams.get("sort");

    if (sort) setCurrentSort(sort);

    const qsBeforeCursor = searchParams.get("beforeCursor");
    const qsAfterCursor = searchParams.get("afterCursor");

    if (qsBeforeCursor) setBeforeCursor(qsBeforeCursor);
    if (qsAfterCursor) setAfterCursor(qsAfterCursor);
  }, []);

  useEffect(() => {
    if (!aspects) return;

    const code = codeUuid || searchParams.get("code");
    const projectStatus = searchParams.get("status");
    const developer = developerUuid || searchParams.get("developer");
    const projectTypes = searchParams.get("projectType");
    const badges = searchParams.get("badges");
    const certifications = searchParams.get("certifications");
    const country = searchParams.get("countryCode");
    const region = searchParams.get("region");
    const tags = searchParams.get("tags");
    const group = searchParams.get("group");
    const habitatType = searchParams.get("habitatType");
    const habitatCondition = searchParams.get("habitatCondition");
    const unitMeasurements = searchParams.get("unitMeasurements");

    const newFilters: Filter[] = [];
    newFilters.push({
      kind: "text",
      name: "search",
      placeholder: "Project or registry name",
    });
    newFilters.push({
      kind: "checkbox",
      name: "unitMeasurements",
      label: "Unit measurements",
      checkboxes: aspects.unitMeasurements
        .filter((x) => x !== "")
        .map((x) => {
          return { label: x, value: x };
        }),
    });
    newFilters.push({
      kind: "slider",
      name: "unitsNumber",
      label: "Number of units",
      min: aspects.calculatedTotalQuantityMin,
      max: aspects.calculatedTotalQuantityMax,
      range: true,
    });
    newFilters.push({
      kind: "checkbox",
      name: "unitsSale",
      label: "Units available for sale",
      checkboxes: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    });

    if (aspects.calculatedIssuancePriceMin && aspects.calculatedIssuancePriceMax) {
      newFilters.push({
        kind: "slider",
        name: "unitPrice",
        label: "Unit price",
        min: aspects.calculatedIssuancePriceMin,
        max: aspects.calculatedIssuancePriceMax,
        range: true,
      });
    }

    if (unitMeasurements && unitMeasurements.includes("Carbon")) {
      newFilters.push({
        kind: "checkbox",
        name: "unitType",
        label: "Unit type",
        tooltipHeader: "What are the unit types?",
        tooltipText:
          "WCUs, PCUs and WCCs are ‘Verified Carbon Units’ which represent units that have been verified. PIUs and EIUs are ‘Pending Issuance Units’ which represent future units that are calculated, but yet to be verified. Each code uses their own term for verified and pending units. You can find out more by visiting the Codes and Standards page.",
        checkboxes: [
          { label: "PIU", value: "piu" },
          { label: "EIU", value: "eiu" },
          { label: "WCU", value: "wcu" },
          { label: "PCU", value: "pcu" },
          { label: "WCC", value: "wcc" },
        ],
      });
      newFilters.push({
        kind: "slider",
        name: "vintage",
        label: "Vintage",
        min: aspects.calculatedVintageEndDateMin?.getFullYear(),
        max: aspects.calculatedVintageEndDateMax?.getFullYear(),
        range: true,
        formatValue: (value) => value?.toString(),
      });
    }

    const codes = aspects.standards.reduce(
      (accumulator, current) => {
        if (!accumulator.some((i) => i.label === current.displayName)) {
          accumulator.push({
            label: current.displayName,
            value: current.uuid,
          });
        }

        return accumulator;
      },
      <{ label: string; value: string }[]>[]
    );

    if (!!code || codes.length > 1) {
      newFilters.push({
        kind: "checkbox",
        name: "code",
        label: "Codes and Standards",
        checkboxes: aspects.standards.reduce(
          (accumulator, current) => {
            if (!accumulator.some((i) => i.label === current.displayName)) {
              accumulator.push({
                label: current.displayName,
                value: current.uuid,
              });
            }

            return accumulator;
          },
          <{ label: string; value: string }[]>[]
        ),
      });
    }

    if (unitMeasurements && unitMeasurements.includes("Biodiversity")) {
      const habitatTypeFilterValues = aspects.habitatTypes.map((x) => ({
        key: x,
        value: x,
      }));

      if (!!habitatType || habitatTypeFilterValues.length > 0) {
        newFilters.push({
          kind: "select",
          name: "habitatType",
          label: "Habitat type",
          data: habitatTypeFilterValues,
        });
      }

      const habitatConditionFilterValues = aspects.habitatConditions.map((x) => ({
        key: x,
        value: x,
      }));

      if (!!habitatCondition || habitatConditionFilterValues.length > 0) {
        newFilters.push({
          kind: "select",
          name: "habitatCondition",
          label: "Habitat condition",
          data: habitatConditionFilterValues,
        });
      }
    }

    const statuses = aspects.statuses.map((status) => ({
      key: status,
      value: status,
    }));

    if (!!projectStatus || statuses.length > 1) {
      newFilters.push({
        kind: "select",
        name: "status",
        label: "Registry status",
        data: statuses,
      });
    }

    if (!developerUuid) {
      const developers = aspects.developers.reduce((accumulator, current) => {
        if (!accumulator.some((i) => i.key === current.uuid)) {
          accumulator.push({
            key: current.uuid,
            value: current.displayName,
          });
        }

        return accumulator;
      }, [] as SelectData);

      if (!!developer || developers.length > 1) {
        newFilters.push({
          kind: "autocomplete",
          name: "developer",
          label: "Developer",
          data: developers,
        });
      }
    }

    const projectTypeFilterValues = aspects.projectTypes.map((projectType) => ({
      key: projectType.uuid,
      value: projectType.displayName,
    }));

    if (!!projectTypes || projectTypeFilterValues.length > 1) {
      newFilters.push({
        kind: "select",
        name: "projectType",
        label: "Project type",
        data: projectTypeFilterValues,
      });
    }

    const badgeFilterValues = aspects.badges.map((badge) => ({
      key: badge,
      value: badge,
    }));

    if (!!badges || badgeFilterValues.length > 0) {
      newFilters.push({
        kind: "select",
        name: "badges",
        label: "Badges",
        data: aspects.badges.map((badge) => ({
          key: badge.uuid,
          value: badge.displayName,
        })),
      });
    }

    const certificationFilterValues = aspects.certifications.map((certification) => ({
      key: certification,
      value: certification,
    }));

    if (!!certifications || certificationFilterValues.length > 0) {
      newFilters.push({
        kind: "select",
        name: "certifications",
        label: "Certifications",
        data: aspects.certifications.map((certification) => ({
          key: certification.uuid,
          value: certification.displayName,
        })),
      });
    }

    const countries = aspects.addressCountryCodes.map((countryCode) => ({
      key: countryCode,
      value: getCountryNameByCode(countryCode) || countryCode,
    }));

    if (!!country || countries.length > 1) {
      newFilters.push({
        kind: "select",
        name: "countryCode",
        label: "Country",
        data: countries,
      });
    }

    const regions = (country ? aspects.regions.filter((reg) => reg.addressCountryCode === country) : aspects.regions)
      .flatMap((reg) => reg.addressRegions)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b))
      .map((reg) => ({
        key: reg,
        // TODO KAN-4532 remove this after BE FIX
        value: reg.replaceAll("�", "ô"),
      }));

    if (!!region || regions.length > 1) {
      newFilters.push({
        kind: "autocomplete",
        name: "region",
        label: "County / Region / State",
        data: regions,
      });
    }

    const tagFilterValues = aspects.tags.map((tag) => ({
      key: tag,
      value: tag,
    }));

    if (!!tags || tagFilterValues.length > 1) {
      newFilters.push({
        kind: "select",
        name: "tags",
        label: "Tags",
        data: aspects.tags.map((tag) => ({
          key: tag,
          value: tag,
        })),
      });
    }

    if (group) {
      newFilters.push({
        kind: "hidden",
        name: "group",
        data: group,
      });
    }

    // newFilters.push({
    //   kind: "checkbox",
    //   name: "seekingFunding",
    //   label: "Seeking funding",
    //   checkboxes: [
    //     { label: "Yes", value: "yes" },
    //     { label: "No", value: "no" },
    //   ],
    // });

    newFilters.push({
      kind: "checkbox",
      name: "pilotProject",
      label: "Pilot project",
      checkboxes: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    });

    setFilters(newFilters);
  }, [aspects]);

  const loadProjects = useCallback(async () => {
    const request: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]> = {
      paging: {
        limit: pageSize,
        beforeCursor: null,
        afterCursor: null,
      },
    };

    const search = searchParams.get("search");
    const unitsNumber = searchParams.get("unitsNumber");
    const unitsSale = searchParams.get("unitsSale");
    // const seekingFunding = searchParams.get("seekingFunding");
    const pilotProject = searchParams.get("pilotProject");
    const unitPrice = searchParams.get("unitPrice");
    const unitType = searchParams.get("unitType");
    const vintage = searchParams.get("vintage");
    const code = searchParams.get("code");
    const status = searchParams.get("status");
    const developer = developerUuid || searchParams.get("developer");
    const projectType = searchParams.get("projectType");
    const badges = searchParams.get("badges");
    const certifications = searchParams.get("certifications");
    const countryCode = searchParams.get("countryCode");
    const region = searchParams.get("region");
    const tags = searchParams.get("tags");
    const sort = searchParams.get("sort");
    const group = searchParams.get("group");
    const unitMeasurements = searchParams.get("unitMeasurements");
    const habitatType = searchParams.get("habitatType");
    const habitatCondition = searchParams.get("habitatCondition");

    request.filter = {};
    request.filter.results = {};

    if (search) {
      request.filterGroups = [
        {
          operator: "or",
          filter: {
            results: {
              displayName: {
                operator: "contains",
                value: search,
              },
              name: {
                operator: "contains",
                value: search,
              },
            },
          },
        },
      ];
    }

    if (unitsNumber) {
      const [calculatedTotalQuantityMin, calculatedTotalQuantityMax] = unitsNumber.split(",");
      request.filter.results.calculatedTotalQuantityMin = {
        operator: "gte",
        value: parseInt(calculatedTotalQuantityMin, 10),
      };
      request.filter.results.calculatedTotalQuantityMax = {
        operator: "lte",
        value: parseInt(calculatedTotalQuantityMax, 10),
      };
    }

    if (unitsSale && unitsSale.includes("false")) {
      request.filter.results.listing = {
        availableForSale: {
          operator: "eq",
          value: unitsSale.includes("yes:true"),
        },
      };
    }

    // if (seekingFunding && seekingFunding.includes("false")) {
    //   request.filter.results.listing = {
    //     seekingFunding: {
    //       operator: "eq",
    //       value: seekingFunding.includes("yes:true"),
    //     },
    //   };
    // }

    if (pilotProject && pilotProject.includes("false")) {
      request.filter.results.listing = {
        pilotProject: {
          operator: "eq",
          value: pilotProject.includes("yes:true"),
        },
      };
    }

    if (unitPrice) {
      const [calculatedIssuancePriceMin, calculatedIssuancePriceMax] = unitPrice.split(",");
      request.filter.results.calculatedIssuancePriceMax = {
        operator: "gte",
        value: parseInt(calculatedIssuancePriceMin, 10),
      };
      request.filter.results.calculatedIssuancePriceMin = {
        operator: "lte",
        value: parseInt(calculatedIssuancePriceMax, 10),
      };
    }

    if (unitType && unitType.includes("true")) {
      const unitTypes = unitType.split(",").reduce<string[]>((accumulator, current) => {
        const [key, value] = current.split(":");
        if (!accumulator.includes(key) && value === "true") {
          accumulator.push(key);
        }
        return accumulator;
      }, []);

      if (unitTypes && unitTypes.length > 0) {
        const unitTypesFilter: string[] = [];
        if (unitTypes.includes("wcu")) {
          unitTypesFilter.push("WCU + PIU", "PIU + WCU");
          unitTypesFilter.push("WCU");
        }
        if (unitTypes.includes("piu")) {
          unitTypesFilter.push("WCU + PIU", "PIU + WCU", "PIU + PCU", "PCU + PIU");
          unitTypesFilter.push("PIU");
        }
        if (unitTypes.includes("eiu")) {
          unitTypesFilter.push("EIU + WCC", "WCC + EIU");
          unitTypesFilter.push("EIU");
        }
        if (unitTypes.includes("pcu")) {
          unitTypesFilter.push("PIU + PCU", "PCU + PIU");
          unitTypesFilter.push("PCU");
        }
        if (unitTypes.includes("wcc")) {
          unitTypesFilter.push("EIU + WCC", "WCC + EIU");
          unitTypesFilter.push("WCC");
        }
        const unitTypesFilterSet = new Set(unitTypesFilter);
        request.filter.results.cachedQuantityType = {
          operator: "in",
          value: Array.from(unitTypesFilterSet),
        };
      }
    }

    if (vintage) {
      const [calculatedVintageEndDateMin, calculatedVintageEndDateMax] = vintage.split(",");

      const [minYear, minMonth, minDay] = calculatedVintageEndDateMax.split("-");
      request.filter.results.calculatedVintageEndDateMin = {
        operator: "lte",
        value: new Date(parseInt(minYear, 10), parseInt(minMonth, 10), parseInt(minDay, 10)),
      };

      const [maxYear, maxMonth, maxDay] = calculatedVintageEndDateMin.split("-");
      request.filter.results.calculatedVintageEndDateMax = {
        operator: "gte",
        value: new Date(parseInt(maxYear, 10), parseInt(maxMonth, 10), parseInt(maxDay, 10)),
      };
    }

    if (code && code.includes("false")) {
      request.filter.results.standard = {
        uuid: {
          operator: "in",
          value: code.split(",").reduce<string[]>((accumulator, current) => {
            const [key, value] = current.split(":");

            if (!accumulator.includes(key) && value === "true") {
              accumulator.push(key);
            }

            return accumulator;
          }, []),
        },
      };
    }

    if (status) {
      request.filter.results.status = {
        operator: "eq",
        value: status,
      };
    }

    if (developer) {
      request.filter.results.developer = {
        uuid: {
          operator: "eq",
          value: developer,
        },
      };
    }

    if (projectType) {
      request.filter.results.projectType = {
        uuid: {
          operator: "eq",
          value: projectType,
        },
      };
    }

    if (badges) {
      request.badgeUuid = badges;
    }

    if (certifications) {
      request.certificationUuid = certifications;
    }

    if (countryCode) {
      request.filter.results.addressCountryCode = {
        operator: "eq",
        value: countryCode,
      };
    }

    if (region) {
      request.filter.results.addressRegion = {
        operator: "eq",
        value: region,
      };
    }

    if (tags) {
      request.filter.results.tags = { operator: "eq", value: tags };
    }

    if (group) {
      request.filter.results.groups = {
        groupUuid: {
          operator: "eq",
          value: group,
        },
      };
    }

    if (unitMeasurements) {
      const selectedUnitMeasurements = unitMeasurements.split(",").filter(Boolean);
      if (selectedUnitMeasurements.length > 0) {
        request.filter.results.unitMeasurements = {
          some: {
            operator: "in",
            value: selectedUnitMeasurements,
          },
        };
      }
    }

    if (habitatCondition) {
      request.habitatCondition = habitatCondition;
    }

    if (habitatType) {
      request.habitatType = habitatType;
    }

    const sortOrder = sort || currentSort;
    if (sortOrder) {
      const [key, direction] = sortOrder.split("|");

      request.sort = [
        {
          key: key as never,
          direction: direction as SortDirection,
        },
        {
          key: "results.id",
          direction: "asc",
        },
      ];
    }

    if (beforeCursor) {
      request.paging.beforeCursor = beforeCursor;
    } else if (afterCursor) {
      request.paging.afterCursor = afterCursor;
    }

    const response = await searchProjects(request);

    if (response.status === Status.Success && response.data) {
      if (response.data.aspects) {
        setAspects(response.data.aspects);
      }

      setTotalCount(response.data.paging.total || 0);
      setStartCursor(response.data.paging.startCursor);
      setEndCursor(response.data.paging.endCursor);
      setHasPrevious(response.data.paging.hasPreviousPage);
      setHasNext(response.data.paging.hasNextPage);
      setProjects(
        response.data.results?.map((d) => ({
          uuid: d.uuid,
          name: d.displayName,
          status: d.status,
          imageSource:
            d?.listing?.content?.images && d?.listing?.content?.images[0]
              ? d.listingFiles?.find((el) => el.uuid === d?.listing?.content?.images[0])?.file.url
              : "",
          hasUnits: !!d.listing?.availableForSale,
          hasPriceInDirectory: !!d.listing?.showPriceInDirectory,
          seekingFunding: !!d.listing?.seekingFunding,
          pilotProject: !!d.listing?.pilotProject,
          addressCountryCode: d.addressCountryCode || "",
          developerSmallLogoFileUrl: d.developer?.files ? d.developer.files[0]?.file.url : "",
          developerDisplayName: d.developer?.displayName || undefined,
          standardDisplayName: d.standard?.displayName || undefined,
          listingContentShortDescription: d.listing?.content?.shortDescription,
          calculatedIssuancePriceMin: d.calculatedIssuancePriceMin,
          calculatedIssuancePriceMax: d.calculatedIssuancePriceMax,
          calculatedTotalQuantityMin: d.calculatedTotalQuantityMin,
          calculatedTotalQuantityMax: d.calculatedTotalQuantityMax,
          cachedTotalQuantity: d.cachedUnitTotal || 0,
          quantityType: d.cachedQuantityType,
          tags: d.tags,
          badges: d.badges,
          certifications: d.certifications,
          projectType: d.projectType,
          isCodeCompliant:
            d.status !== ProjectStatusConstants.DRAFT && d.status !== ProjectStatusConstants.PRE_DEVELOPMENT,
        })) || []
      );
    }
  }, [aspects, searchParams, beforeCursor, afterCursor]);

  useEffect(() => {
    loadProjects();
  }, [searchParams, beforeCursor, afterCursor]);

  useEffect(() => {
    if (!aspects) return;

    if (currentFilter) {
      const {
        search,
        unitsNumber,
        unitsSale,
        // seekingFunding,
        pilotProject,
        unitPrice,
        unitType,
        vintage,
        code,
        status,
        developer,
        projectType,
        badges,
        certifications,
        countryCode,
        region,
        tags,
        group,
        unitMeasurements,
        habitatCondition,
        habitatType,
      } = currentFilter;

      if (search) {
        searchParams.set("search", search);
      } else searchParams.delete("search");

      if (unitsNumber) {
        searchParams.set("unitsNumber", `${unitsNumber[0]},${unitsNumber[1]}`);
      } else {
        searchParams.delete("unitsNumber");
      }

      if (unitsSale) {
        const keys = Object.keys(unitsSale);

        if (keys.some((key) => unitsSale[key])) {
          searchParams.set("unitsSale", keys.map((key) => `${key}:${unitsSale[key]}`).join(","));
        } else {
          searchParams.delete("unitsSale");
        }
      } else {
        searchParams.delete("unitsSale");
      }

      // if (seekingFunding) {
      //   const keys = Object.keys(seekingFunding);

      //   if (keys.some((key) => seekingFunding[key])) {
      //     searchParams.set("seekingFunding", keys.map((key) => `${key}:${seekingFunding[key]}`).join(","));
      //   } else {
      //     searchParams.delete("seekingFunding");
      //   }
      // } else {
      //   searchParams.delete("seekingFunding");
      // }

      if (pilotProject) {
        const keys = Object.keys(pilotProject);

        if (keys.some((key) => pilotProject[key])) {
          searchParams.set("pilotProject", keys.map((key) => `${key}:${pilotProject[key]}`).join(","));
        } else {
          searchParams.delete("pilotProject");
        }
      } else {
        searchParams.delete("pilotProject");
      }

      if (unitPrice) {
        searchParams.set("unitPrice", `${unitPrice[0]},${unitPrice[1]}`);
      } else {
        searchParams.delete("unitPrice");
      }

      if (unitType) {
        const keys = Object.keys(unitType);

        if (keys.some((key) => unitType[key])) {
          searchParams.set("unitType", keys.map((key) => `${key}:${unitType[key]}`).join(","));
        } else {
          searchParams.delete("unitType");
        }
      } else {
        searchParams.delete("unitType");
      }

      if (vintage) {
        searchParams.set(
          "vintage",
          `${vintage[0].toString()}-${aspects.calculatedVintageEndDateMin.getMonth()}-${aspects.calculatedVintageEndDateMin.getDay()},` +
            `${vintage[1].toString()}-${aspects.calculatedVintageEndDateMax.getMonth()}-${aspects.calculatedVintageEndDateMax.getDay()}`
        );
      } else {
        searchParams.delete("vintage");
      }

      if (code) {
        const keys = Object.keys(code);

        if (keys.some((key) => code[key])) {
          searchParams.set("code", keys.map((key) => `${key}:${code[key]}`).join(","));
        } else {
          searchParams.delete("code");
        }
      } else {
        searchParams.delete("code");
      }

      if (status) {
        searchParams.set("status", status);
      } else searchParams.delete("status");

      if (developer) {
        searchParams.set("developer", developer);
      } else searchParams.delete("developer");

      if (projectType) {
        searchParams.set("projectType", projectType);
      } else searchParams.delete("projectType");

      if (badges) {
        searchParams.set("badges", badges);
      } else searchParams.delete("badges");

      if (certifications) {
        searchParams.set("certifications", certifications);
      } else searchParams.delete("certifications");

      if (countryCode) {
        searchParams.set("countryCode", countryCode);
      } else searchParams.delete("countryCode");

      if (region) {
        searchParams.set("region", region);
      } else searchParams.delete("region");

      if (tags) {
        searchParams.set("tags", tags);
      } else searchParams.delete("tags");

      if (group) {
        searchParams.set("group", group);
      } else searchParams.delete("group");

      if (unitMeasurements) {
        const keys = Object.keys(unitMeasurements).filter((key) => unitMeasurements[key]);
        if (keys.length > 0) {
          searchParams.set("unitMeasurements", keys.join(","));
          if (!keys.includes("Biodiversity")) {
            searchParams.delete("habitatType");
            searchParams.delete("habitatCondition");
          }
          if (!keys.includes("Carbon")) {
            searchParams.delete("vintage");
            searchParams.delete("unitType");
          }
        } else {
          searchParams.delete("unitMeasurements");
          searchParams.delete("habitatType");
          searchParams.delete("habitatCondition");
          searchParams.delete("vintage");
          searchParams.delete("unitType");
        }
      } else {
        searchParams.delete("unitMeasurements");
        searchParams.delete("habitatType");
        searchParams.delete("habitatCondition");
        searchParams.delete("vintage");
        searchParams.delete("unitType");
      }

      if (habitatCondition) {
        searchParams.set("habitatCondition", habitatCondition);
      } else searchParams.delete("habitatCondition");

      if (habitatType) {
        searchParams.set("habitatType", habitatType);
      } else searchParams.delete("habitatType");
    }

    if (currentSort && currentSort !== sorting[0].key) {
      searchParams.set("sort", currentSort);
    } else searchParams.delete("sort");

    if (beforeCursor) searchParams.set("beforeCursor", beforeCursor);
    else searchParams.delete("beforeCursor");

    if (afterCursor) searchParams.set("afterCursor", afterCursor);
    else searchParams.delete("afterCursor");

    setSearchParams(searchParams);
  }, [debounceValue, currentSort, beforeCursor, afterCursor]);

  const onFiltersChange = (value: ProjectsFilter): void => {
    setCurrentFilter(value);
    // reset paging when changing filtering
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
  };

  const onSortingChange = (value: string): void => {
    setCurrentSort(value);
    // reset paging when changing filtering
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
  };

  const onProjectClick = (el: DisplayProject): void => {
    navigate({
      pathname: getPublicProjectOverviewRoute(el.uuid),
      search: searchParams.toString(),
    });
  };

  return {
    filters,
    currentFilter,
    totalCount,
    currentSort,
    sorting,
    projects,
    onFiltersChange,
    onSortingChange,
    onProjectClick,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
  };
};
